import { ConvexReactClient, type MutationOptions } from 'convex/react';
import {
  getFunctionName,
  type ArgsAndOptions,
  type FunctionArgs,
  type FunctionReference,
  type FunctionReturnType,
  type OptionalRestArgs,
} from 'convex/server';
import { trackUnsavedChange } from './unsaved-changes';

// These requests have no unsaved traveler input. Reconciliation operates entirely
// on already-saved data on the server; it does not need the tab to apply its result.
// Keep all other actions/mutations protected by default, including save and Undo.
const backgroundActions = new Set(['airportApproach:estimateRoute', 'travelSchedule:reconcileDay']);
const backgroundMutations = new Set([
  'tripCollaboration:heartbeatEditingPresence',
  // This schedules server work from saved participation and itinerary rows.
  'personalPlanRepairs:ensure',
]);

export class RoamConvexClient extends ConvexReactClient {
  constructor(url: string) {
    super(url, { unsavedChangesWarning: false });
  }

  override mutation<Mutation extends FunctionReference<'mutation'>>(
    mutation: Mutation,
    ...args: ArgsAndOptions<Mutation, MutationOptions<FunctionArgs<Mutation>>>
  ): Promise<FunctionReturnType<Mutation>> {
    const run = () => super.mutation(mutation, ...args);
    return backgroundMutations.has(getFunctionName(mutation)) ? run() : trackUnsavedChange(run);
  }

  override action<Action extends FunctionReference<'action'>>(
    action: Action,
    ...args: OptionalRestArgs<Action>
  ): Promise<FunctionReturnType<Action>> {
    const run = () => super.action(action, ...args);
    return backgroundActions.has(getFunctionName(action)) ? run() : trackUnsavedChange(run);
  }
}
